import { useContext, useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '@/App';
import Navbar from '@/components/Navbar';
import { Crown, CheckCircle, Loader2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SubscriptionSuccess = () => {
  const { token, refreshUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking'); // checking, paid, failed

  const pollStatus = useCallback(async (attempts = 0) => {
    if (!sessionId || !token || attempts >= 5) {
      if (attempts >= 5) setStatus('failed');
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/payments/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.payment_status === 'paid') {
        setStatus('paid');
        if (refreshUser) refreshUser();
      } else if (data.status === 'expired') {
        setStatus('failed');
      } else {
        setTimeout(() => pollStatus(attempts + 1), 2000);
      }
    } catch {
      setTimeout(() => pollStatus(attempts + 1), 2000);
    }
  }, [sessionId, token, refreshUser]);

  useEffect(() => {
    if (sessionId) pollStatus();
    else setStatus('failed');
  }, [sessionId, pollStatus]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-peach-50 to-teal-50">
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 py-8 pt-24">
        <div className="glass-dark rounded-3xl p-12 text-center shadow-2xl">

          {status === 'checking' && (
            <div data-testid="payment-checking">
              <Loader2 className="w-16 h-16 text-gold mx-auto mb-6 animate-spin" />
              <h2 className="text-3xl font-bold mb-4">Verifying Payment...</h2>
              <p className="text-gray-600 text-lg">Please wait while we confirm your payment.</p>
            </div>
          )}

          {status === 'paid' && (
            <div data-testid="payment-success">
              <div className="w-20 h-20 bg-gradient-to-br from-gold to-gold-dark rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-12 h-12 text-white" />
              </div>
              <h2 className="text-3xl font-bold mb-4">Welcome to Premium!</h2>
              <p className="text-gray-600 text-lg mb-8">
                Your payment was successful. All premium features are now active!
              </p>
              <div className="flex items-center justify-center space-x-2 mb-8 text-gold">
                <Crown className="w-8 h-8" />
                <span className="text-2xl font-bold">Premium Member</span>
              </div>
              <div className="space-y-3">
                <Button
                  data-testid="goto-dashboard-btn"
                  onClick={() => navigate('/dashboard')}
                  className="w-full py-6 rounded-xl bg-gradient-to-r from-gold to-gold-dark text-white text-lg"
                >
                  Go to Dashboard
                </Button>
                <Button
                  onClick={() => navigate('/discover')}
                  variant="outline"
                  className="w-full py-6 rounded-xl border-2 border-gold text-gold hover:bg-gold hover:text-white text-lg"
                >
                  Start Discovering
                </Button>
              </div>
            </div>
          )}

          {status === 'failed' && (
            <div data-testid="payment-failed">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-12 h-12 text-red-500" />
              </div>
              <h2 className="text-3xl font-bold mb-4">Payment Issue</h2>
              <p className="text-gray-600 text-lg mb-8">
                We couldn't verify your payment. Please try again or contact support.
              </p>
              <Button
                onClick={() => navigate('/subscription')}
                className="w-full py-6 rounded-xl bg-gold text-white text-lg"
              >
                Try Again
              </Button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default SubscriptionSuccess;
